import { Button, media, ModalWrapper, styled } from '@payright/web-components';
import { bnplContractTemplate, type BNPLContractProps } from '@payright/templates';
import { useCallback, useEffect, useRef, useState } from 'react';
import IframeResizer from 'iframe-resizer-react';
import { SupportPhoneNumber } from '../../../components/phone-numbers/SupportPhoneNumber';

const SCContractContent = styled.div`
  display: grid;
  gap: 1rem;
  overflow-y: scroll;
  border-radius: 0.375rem;
  height: calc(100vh - 10rem);
  max-width: 800px;
  background-color: ${(props) => props.theme.colours.secondary3};

  @supports (max-height: 100svh) {
    height: calc(100svh - 10rem);
  }
`;

const SCIframeWrapper = styled.div`
  overflow-x: auto;
  background-color: ${(props) => props.theme.colours.white};
`;

const SCIframeResizer = styled(IframeResizer)`
  border-bottom-right-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
  border-width: 0;
  background-color: #ffffff;
  width: 100%;
`;

const SCModalContent = styled.div`
  display: grid;
  gap: 1rem;
  background-color: ${(props) => props.theme.colours.secondary3};
  padding: 0.5rem;

  ${media.min.medium} {
    padding: 1.5rem;
  }
`;

const SCHeading = styled.h4`
  text-align: center;
  display: none;
`;

const SCButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: row-reverse;

  /* this was copied from pplus and does not align with an existing size model which uses width rather than height */
  @media (min-height: 540px) {
    flex-wrap: wrap;
  }

  ${media.min.medium} {
    flex-wrap: nowrap;
  }

  ${media.max.medium} {
    button {
      width: 100%;
    }
  }
`;

const SCDescription = styled.div`
  font-size: 0.875rem;
  line-height: 1.25rem;
  display: grid;
  gap: 0.5rem;

  ol {
    display: grid;
    gap: 0.5rem;
    list-style: decimal;
    list-style-type: decimal;
    margin-left: 1rem;
    color: ${(props) => props.theme.colours.grey.xdark};
  }
`;

type ContractModalProps = {
  address: BNPLContractProps['address'];
  currentDate: Date;
  customer: BNPLContractProps['customer'];
  onDecline: () => void;
  handleSubmit: () => void;
  isLoading?: boolean;
  plan: BNPLContractProps['plan'];
  store: BNPLContractProps['store'];
};

const isOnBottom = (scrollElement: HTMLElement) => {
  const safetyOffsetPx = 20;
  return (
    scrollElement.scrollHeight - scrollElement.scrollTop <=
    scrollElement.clientHeight + safetyOffsetPx
  );
};

export const ContractModal = ({
  address,
  currentDate,
  customer,
  handleSubmit,
  onDecline,
  isLoading,
  plan,
  store,
}: ContractModalProps) => {
  const [hasReadContract, setHasReadContract] = useState(false);
  const scrollingDivRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeHeight, setIframeHeight] = useState(400);

  const handleScroll = useCallback(
    (e: Event) => {
      const scrollElement = e.target as HTMLElement;
      if (isOnBottom(scrollElement)) setHasReadContract(true);
    },
    [setHasReadContract]
  );
  const handleResize = useCallback(() => {
    const scrollElement = scrollingDivRef.current;
    if (!scrollElement) return;
    if (isOnBottom(scrollElement)) setHasReadContract(true);
  }, [setHasReadContract]);

  useEffect(() => {
    const current = scrollingDivRef.current;
    current?.addEventListener('scroll', handleScroll);
    window?.addEventListener('resize', handleResize);
    return () => {
      current?.removeEventListener('scroll', handleScroll);
      window?.removeEventListener('resize', handleResize);
    };
  }, [scrollingDivRef.current, handleScroll, handleResize]);

  return (
    <ModalWrapper open={true} background="dark" contentPadding="0.4rem">
      <SCModalContent>
        <SCHeading>Loan Contract</SCHeading>
        <SCContractContent ref={scrollingDivRef}>
          <SCIframeWrapper
            style={{
              height: `${Number(iframeHeight) + 24}px`,
            }}
          >
            <SCIframeResizer
              minHeight={400} // in case the iframe resizer library fails
              id="contract_iframe"
              title="Loan Contract"
              heightCalculationMethod="lowestElement"
              tolerance={4}
              checkOrigin={false}
              onResized={(e) => {
                setIframeHeight(e.height);
              }}
              srcDoc={bnplContractTemplate({ address, currentDate, customer, plan, store })}
            />
          </SCIframeWrapper>
          <SCDescription>
            <p>By clicking “I agree”, you have made to us the following declarations:</p>
            <ol>
              <li>
                You have carefully read this BNPL contract and the accompanying terms and conditions
                and understand that each document between you and us establishes a legal contract
                between you and us. If you have any questions, ask us on <SupportPhoneNumber />{' '}
                before you sign.
              </li>
              <li>
                All information provided in connection with this application is accurate and not
                misleading. You acknowledge that we are relying on that information to enter into
                this BNPL contract.
              </li>
              <li>
                Other than this BNPL contract and the accompanying terms and conditions, you have
                not relied on any promise or representation by anybody when deciding to enter this
                transaction.{' '}
              </li>
              <li>
                The plan will be used only for the purpose set out above under ‘BNPL Contract
                Purpose’.
              </li>
            </ol>
          </SCDescription>
        </SCContractContent>
        <SCButtons>
          <Button
            handleClick={handleSubmit}
            withShadow
            disabled={isLoading || !hasReadContract}
            maxWidth="100%"
            iconPosition="right"
          >
            {hasReadContract ? 'I Agree' : 'Scroll To Accept'}
          </Button>
          <Button handleClick={onDecline} outline colour="blue" maxWidth="100%">
            Decline
          </Button>
        </SCButtons>
      </SCModalContent>
    </ModalWrapper>
  );
};
